import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectBatchProgress,
  resetBatchProgress,
} from '../../../features/patches/patchesSlice';
import { BatchStatusIndicator } from './index';
// Note: PendingChangesView import removed as it's no longer used in this component
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  Divider,
  Snackbar,
} from '@mui/material';
import { PatchType, Store } from '../../../types';

import { PatchTypeSelector } from '../PatchTypeSelector';

type PatchListProps = {
  stores: Store[];
  storeNumbers: string[];
  patchType: PatchType;
  handlePatchTypeChange: (patchType: PatchType) => void;
  getPatchIdsForType?: (patchType: PatchType) => string[];
  getPatchDataForType?: (patchType: PatchType) => any[];
};

const PatchList: React.FC<PatchListProps> = ({
  stores,
  storeNumbers,
  patchType,
  handlePatchTypeChange,
  getPatchIdsForType,
  getPatchDataForType,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showBatchToast, setShowBatchToast] = useState(false);
  const batchProgress = useSelector(selectBatchProgress);
  const dispatch = useDispatch();
  // Removed useExtractedData state, not needed
  const [filteredPatchData, setFilteredPatchData] = useState<any[]>([]);
  const [patchFranchiseCodes] = useState<Record<string, string>>({});
  
  // Auto-reload patch list on patchType or storeNumbers change
  useEffect(() => {
    if (getPatchDataForType) {
      let patchesForType = getPatchDataForType(patchType);

      // Prefer short patch name (e.g., 000000_SetProdPrices) if available
      const getShortPatchName = (patch: any) => {
        if (patch.name && /^\d+_/.test(patch.name)) return patch.name;
        if (patch.patchName && /^\d+_/.test(patch.patchName)) return patch.patchName;
        return patch.name || patch.patchName || '';
      };

      // Sort patches to prefer short name first
      patchesForType = patchesForType.slice().sort((a, b) => {
        const aShort = /^\d+_/.test(getShortPatchName(a));
        const bShort = /^\d+_/.test(getShortPatchName(b));
        if (aShort && !bShort) return -1;
        if (!aShort && bShort) return 1;
        return getShortPatchName(a).localeCompare(getShortPatchName(b));
      });

  setFilteredPatchData(patchesForType);
    } else {
  setFilteredPatchData([]);
    }
  }, [getPatchDataForType, patchType, storeNumbers]);

  // Reset batch progress when component unmounts or filters change
  useEffect(() => {
    return () => {
      dispatch(resetBatchProgress());
    };
  }, [dispatch]);

  // Show toast when batch progress is complete
  useEffect(() => {
    if (batchProgress.isComplete && batchProgress.total > 0) {
      setShowBatchToast(true);
    }
  }, [batchProgress.isComplete, batchProgress.total]);

  // Handle closing the toast
  const handleCloseToast = () => {
    setShowBatchToast(false);
  };

  // No menu channel or fetch patches handler needed

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  // Always show the patch list, auto-loads on patchType/storeNumbers change


  // Use only extracted/prop patch data
  const patches = filteredPatchData;
  const patchCount = patches.length;

  // No patches found
  if (patchCount === 0) {
    return (
      <Paper sx={{ width: '100%', mb: 2, p: 2 }} data-testid='noPatchesFoundPaper'>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Patch Type:
            </Typography>
            <PatchTypeSelector
              value={patchType}
              onChange={handlePatchTypeChange}
              variant='select'
            />
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Alert severity='info'>
          No patches found for the selected stores and patch type.
        </Alert>
      </Paper>
    );
  }

  // Render the patches in a table with batch status indicator
  // If using new pendingOps structure, pass enriched ops for display
  // TODO: Replace this with the correct pendingOps from useBulkUpdate if available in this scope
  // For now, fallback to old logic if not refactored yet
  return (
    <Box>
      <Paper sx={{ width: '100%', mb: 2 }} data-testid='patchListPaper'>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant='subtitle2' gutterBottom>
                Patch Type:
              </Typography>
              <PatchTypeSelector
                value={patchType}
                onChange={handlePatchTypeChange}
                variant='select'
              />
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table size='medium'>
            <TableHead>
              <TableRow>
                <TableCell>Patch Name</TableCell>
                <TableCell>Patch ID</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>FZ Code</TableCell>
                <TableCell>Total Operations</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((patch: any) => {
                const totalOperations = patch.operations ? patch.operations.length : '—';
                let store = stores.find((s) =>
                  s.assignedMenuDefinitions?.some((menuDef) =>
                    menuDef.patches?.some((p) => p.patchId === patch.patchId)
                  )
                );
                if (!store && patch.storeNumber) {
                  store = stores.find((s) => s.storeNumber === patch.storeNumber);
                }
                if (!store && patch.storeNumbers && patch.storeNumbers.length > 0) {
                  store = stores.find((s) => s.storeNumber === patch.storeNumbers[0]);
                }
                let extractedStoreNumber: string | null = null;
                if (patch.name) {
                  const patchNameMatch = patch.name.match(/^(\d+)_/);
                  if (patchNameMatch && patchNameMatch[1]) {
                    extractedStoreNumber = patchNameMatch[1];
                  }
                }
                const storeNumber =
                  store?.storeNumber ||
                  patch.storeNumber ||
                  extractedStoreNumber ||
                  (patch.storeNumbers && patch.storeNumbers.length > 0 ? patch.storeNumbers[0] : 'N/A');
                const franchiseCode = patchFranchiseCodes[patch.patchId] || 'N/A';
                const getShortPatchName = (patch: any) => {
                  if (patch.name && /^\d+_/.test(patch.name)) return patch.name;
                  if (patch.patchName && /^\d+_/.test(patch.patchName)) return patch.patchName;
                  return patch.name || patch.patchName || `Patch ${patch.patchId}`;
                };
                return (
                  <TableRow key={patch.patchId}>
                    <TableCell>{getShortPatchName(patch)}</TableCell>
                    <TableCell>{patch.patchId}</TableCell>
                    <TableCell>
                      {storeNumber !== 'N/A' ? <Chip label={storeNumber} size='small' variant='outlined' /> : 'N/A'}
                    </TableCell>
                    <TableCell>{franchiseCode}</TableCell>
                    <TableCell>{totalOperations}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={patches.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Batch Status Toast */}
      <Snackbar
        open={showBatchToast}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Box sx={{ minWidth: '300px' }}>
          <BatchStatusIndicator
            batchProgress={batchProgress}
            storeCount={storeNumbers.length}
            isLoading={false}
            isFetching={false}
            error={undefined}
          />
        </Box>
      </Snackbar>
    </Box>
  );
};

export default PatchList;
